import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import ModalBusyContainer from 'components/ModalBusyContainer';
import { handleCertificateDownload } from 'utils/certificateDownloadHandler';
import {
  downloadAttemptAssessmentResult,
  downloadTrainingSessionCertificate,
  getTrainingSessionParticipantAttempts,
  grantTrainingSessionParticipantRetake,
} from 'http/trainingSessions';

const useStyles = makeStyles(theme => ({
  header: {
    marginBottom: theme.spacing(2),
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusPassed: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  statusFailed: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  statusPending: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  latest: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85em',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    gap: theme.spacing(1),
  },
  confirmBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const formatDate = value => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString();
};

const AttemptsModal = ({ trainingSessionId, userId, onChanged, closeModal }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [confirmGrant, setConfirmGrant] = useState(false);

  const [loadState, loadAttempts] = useAsyncFn(async () => {
    return getTrainingSessionParticipantAttempts(trainingSessionId, userId);
  }, [trainingSessionId, userId]);

  const [grantState, grantRetake] = useAsyncFn(async () => {
    const result = await grantTrainingSessionParticipantRetake(trainingSessionId, userId);
    if (!result.error && !(result.statusCode && result.statusCode >= 400)) {
      setConfirmGrant(false);
      await loadAttempts();
      if (onChanged) {
        onChanged();
      }
    }
    return result;
  }, [loadAttempts, onChanged, trainingSessionId, userId]);

  useEffect(() => {
    loadAttempts();
  }, [loadAttempts]);

  const data = loadState.value || {};
  const items = data.items || [];

  const handleDownloadAnswers = async attemptId => {
    await downloadAttemptAssessmentResult(trainingSessionId, userId, attemptId);
  };

  const handleCertificate = async attempt => {
    if (!attempt.hasCertificate) {
      return;
    }
    await handleCertificateDownload({
      certificate: attempt.certificatePreviewUrl ? { previewUrl: attempt.certificatePreviewUrl } : {},
      courseId: trainingSessionId,
      userId,
      downloadCertificate: () => downloadTrainingSessionCertificate(trainingSessionId, userId),
      dispatch,
      t,
    });
  };

  return (
    <Box>
      <Box className={classes.meta}>
        <Typography variant="body2">{data.courseName || '—'}</Typography>
        <Typography variant="body2">{t('trainingSessions.attemptsCount', { count: data.count || 0 })}</Typography>
        <Typography variant="body2">
          {t('trainingSessions.moodleAccessState', {
            state: t(`trainingSessions.moodleAccessStates.${data.moodleAccessState || 'open'}`),
          })}
        </Typography>
      </Box>
      <ModalBusyContainer busy={loadState.loading || grantState.loading}>
        {confirmGrant ? (
          <Box className={classes.confirmBox}>
            <Typography>{t('trainingSessions.grantNewAttemptMessage')}</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('trainingSessions.attemptCompletionDate')}</TableCell>
                <TableCell>{t('tablesLabels.status')}</TableCell>
                <TableCell>{t('trainingSessions.attemptGrade')}</TableCell>
                <TableCell>{t('tablesLabels.certificate')}</TableCell>
                <TableCell>{t('trainingSessions.quizAnswers')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(attempt => (
                <TableRow key={attempt.id}>
                  <TableCell>
                    {formatDate(attempt.finishedAt)}
                    {attempt.isLatest && <span className={classes.latest}>{t('trainingSessions.latestAttempt')}</span>}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        attempt.status === 'passed'
                          ? classes.statusPassed
                          : attempt.status === 'pending'
                          ? classes.statusPending
                          : classes.statusFailed
                      }
                    >
                      {t(`trainingSessions.attemptStatuses.${attempt.status}`)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {attempt.grade}
                    {attempt.passGrade ? ` / ${attempt.passGrade}` : ''}
                  </TableCell>
                  <TableCell>
                    {attempt.hasCertificate ? (
                      <Tooltip title={t('trainingSessions.certificateDownloadTooltip')} placement="top" arrow>
                        <IconButton size="small" color="primary" onClick={() => handleCertificate(attempt)}>
                          <Icon>cloud_download</Icon>
                        </IconButton>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {attempt.hasAssessmentResult ? (
                      <Tooltip title={t('trainingSessions.answersDownloadTooltip')} placement="top" arrow>
                        <IconButton size="small" color="primary" onClick={() => handleDownloadAnswers(attempt.id)}>
                          <Icon>get_app</Icon>
                        </IconButton>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!items.length && (
                <TableRow>
                  <TableCell colSpan={5}>{t('trainingSessions.noAttempts')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </ModalBusyContainer>
      <Box className={classes.footer}>
        <Box>
          {confirmGrant ? (
            <>
              <Button
                color="primary"
                variant="contained"
                onClick={grantRetake}
                disabled={grantState.loading}
                style={{ marginRight: 8 }}
              >
                {t('buttons.yes')}
              </Button>
              <Button variant="outlined" onClick={() => setConfirmGrant(false)} disabled={grantState.loading}>
                {t('buttons.cancel')}
              </Button>
            </>
          ) : (
            data.canGrantNewAttempt && (
              <Button color="primary" variant="outlined" onClick={() => setConfirmGrant(true)}>
                {t('trainingSessions.grantNewAttempt')}
              </Button>
            )
          )}
        </Box>
        {!confirmGrant && (
          <Button variant="contained" onClick={closeModal}>
            {t('buttons.close')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

AttemptsModal.propTypes = {
  trainingSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  studentName: PropTypes.string,
  onChanged: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
};

AttemptsModal.defaultProps = {
  studentName: '',
  onChanged: null,
};

export default AttemptsModal;
